import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { formatDate } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useUsersList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)

  // Table Handlers
  const tableColumns = store.state.currentUser.role.code === 'sa'
    ? [
      { key: 'user', sortable: true },
      { key: 'company_name', sortable: false },
      { key: 'email', sortable: true },
      { key: 'role_name', sortable: true },
      { key: 'phone', sortable: false },
      { key: 'status', sortable: true },
      { key: 'actions' },
    ] : [
      { key: 'user', sortable: true },
      { key: 'email', sortable: true },
      { key: 'role_name', sortable: true },
      { key: 'address', sortable: false },
      { key: 'phone', sortable: false },
      {
        key: 'birthdate',
        label: 'Birth date',
        formatter: formatDate,
        sortable: true,
      },
      { key: 'status', sortable: true },
      { key: 'actions' },
    ]

  const perPage = ref(10)
  const totalUsers = ref(null)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const refetchData = () => {
    refUserListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, roleFilter, planFilter, statusFilter], () => {
    refetchData()
  })

  const getUsers = (ctx, callback) => {
    store
      .dispatch('app-user/getUsers', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        role: roleFilter.value,
        plan: planFilter.value,
        status: statusFilter.value,
        user_id: store.state.currentUser.id,
        user_role: store.state.currentUser.role.code,
      })
      .then(response => {
        const { data, total } = response.data.data

        callback(data)
        totalUsers.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const removeUser = userId => {
    return store.dispatch('app-user/removeUser', { userId })
      .then(response => {
        this.$swal({
          icon: 'success',
          title: 'Deleted!',
          text: response.data.message,
        })
        refetchData()
      })
      .catch(error => {
        if (error.response !== undefined){
          this.$swal({
            icon: 'danger',
            title: 'Delete action failed!',
            text: error.response.data.message,
          })
        }
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'Super Admin') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'Super User') return 'success'
    if (role === 'Salesperson') return 'info'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 1) return 'success'
    if (status === 0) return 'danger'
    return 'primary'
  }

  return {
    getUsers,
    removeUser,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
  }
}
